import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { COOKIE_CONSENT } from '../../helpers/enums';
import { useMobile } from '../../hooks/useMobile';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';

const CookieConsentBanner = ({ onConsentChange, currentStatus }) => {
	const theme = useTheme();
	const { t } = useTranslation('cookieConsentBanner');

	const { isMobile } = useMobile();
	const [isVisible, setIsVisible] = useState(
		currentStatus === COOKIE_CONSENT.PENDING
	);

	const handleAccept = () => {
		onConsentChange(COOKIE_CONSENT.ACCEPTED);
		setIsVisible(false);
	};

	const handleReject = () => {
		onConsentChange(COOKIE_CONSENT.REJECTED);
		setIsVisible(false);
	};

	if (currentStatus !== 'pending' || !isVisible) {
		return null;
	}

	return (
		<Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
			<Paper
				sx={{
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: theme.zIndex.drawer + 1000,
					margin: theme.spacing(2),
					maxWidth: '800px',
					marginLeft: 'auto',
					marginRight: 'auto',
					boxShadow: theme.shadows[6],
					backgroundColor: theme.palette.background.paper,
					border: `1px solid ${theme.palette.divider}`,
					[theme.breakpoints.down('sm')]: {
						margin: theme.spacing(1),
						bottom: theme.spacing(1),
						left: theme.spacing(1),
						right: theme.spacing(1),
						width: 'auto'
					}
				}}
			>
				<Box
					p={isMobile ? 2 : 3}
					display="flex"
					flexDirection={isMobile ? 'column' : 'row'}
					alignItems="center"
					justifyContent="space-between"
					gap={isMobile ? 1 : 2}
				>
					<Box flex={1}>
						<Typography variant={isMobile ? 'body2' : 'body1'} gutterBottom>
							<strong>{t('header')}</strong>
						</Typography>
						<Typography
							variant={isMobile ? 'caption' : 'body2'}
							color="text.secondary"
							sx={{ mb: isMobile ? 1 : 0 }}
						>
							{t('description')}
							<a
								href="/privacy-policy"
								target="_blank"
								rel="noopener noreferrer"
								style={{ color: theme.palette.primary.main }}
							>
								{t('privacyPolicy')}
							</a>
							.
						</Typography>
					</Box>

					<Box display="flex" gap={1}>
						<Button
							variant="outlined"
							onClick={handleReject}
							sx={{ minWidth: isMobile ? 'auto' : '120px' }}
						>
							{t('reject')}
						</Button>
						<Button
							variant="contained"
							onClick={handleAccept}
							sx={{ minWidth: isMobile ? 'auto' : '120px' }}
						>
							{t('accept')}
						</Button>
					</Box>
				</Box>
			</Paper>
		</Slide>
	);
};

CookieConsentBanner.propTypes = {
	onConsentChange: PropTypes.func.isRequired,
	currentStatus: PropTypes.oneOf([
		COOKIE_CONSENT.PENDING,
		COOKIE_CONSENT.ACCEPTED,
		COOKIE_CONSENT.REJECTED
	]).isRequired
};

export default CookieConsentBanner;
